import React, { useState, useEffect, useCallback } from 'react';
import WeekOverview from './widgets/WeekOverview';
import NextPrestation from './widgets/NextPrestation';
import BusinessPlaceholder from './widgets/BusinessPlaceholder';
import TodayPrestation from './widgets/TodayPrestation';
import TeamStatus from './widgets/TeamStatus';
import PendingWorksheets from './widgets/PendingWorksheets';
import AlertsList from './widgets/AlertsList';
import BlockedList from './widgets/BlockedList';
import AvailableUsers from './widgets/AvailableUsers';
import GroupWorkload from './widgets/GroupWorkload';
import WorksheetsToValidate from './widgets/WorksheetsToValidate';
import LeaveRequestsWidget from './widgets/LeaveRequestsWidget';
import AbsentsToday from './widgets/AbsentsToday';
import LeaveStats from './widgets/LeaveStats';
import ConnectedUsers from './widgets/ConnectedUsers';
import WorkforceStatus from './widgets/WorkforceStatus';
import SystemHealth from './widgets/SystemHealth';
import RecentActivity from './widgets/RecentActivity';
import SendRemindersWidget from './widgets/SendRemindersWidget';
import PendingInterimsWidget from './widgets/PendingInterimsWidget';
import TeamCalendarWidget from '../TeamCalendarWidget';
import LeaveCountersWidget from '../LeaveCountersWidget';
import LeaveBalanceWidget from '../LeaveBalanceWidget';
import AnnualizationWidget from '../AnnualizationWidget';
import ForfaitJoursWidget from '../ForfaitJoursWidget';

/**
 * DashboardContainer - Conteneur principal du dashboard
 *
 * Affiche un dashboard personnalisé selon le rôle de l'utilisateur.
 * Layout responsive : 3 colonnes (≥1200px) / 2 colonnes (768-1199px) / 1 colonne (<768px)
 */
const DashboardContainer = ({ user, token, onNavigate }) => {
  const role = user?.role || user?.role_name || 'Employés';
  const userId = user?.id || user?.userId;
  const userPermissions = user?.permissions || [];

  // Vérifier les permissions pour les widgets
  const hasLeaveAccess = userPermissions.some(p =>
    ['leaves.access', 'leaves.create', 'leaves.validate', 'leaves.view_all', 'all'].includes(p)
  );
  const hasExpenseAccess = userPermissions.some(p =>
    ['expenses.access', 'expenses.create', 'expenses.validate', 'expenses.view_all', 'all'].includes(p)
  );
  const hasPlanningView = userPermissions.some(p => ['planning.view', 'all'].includes(p));

  // Wrapper navigation : redirige planning→my-planning pour les utilisateurs sans planning.view
  const smartNavigate = useCallback((page) => {
    if (page === 'planning' && !hasPlanningView) {
      onNavigate && onNavigate('my-planning');
    } else {
      onNavigate && onNavigate(page);
    }
  }, [onNavigate, hasPlanningView]);

  // Détecter la taille d'écran pour le responsive (small/medium/large)
  const [screenSize, setScreenSize] = useState(() => {
    if (typeof window === 'undefined') return 'large';
    const width = window.innerWidth;
    if (width >= 1200) return 'large';
    if (width >= 768) return 'medium';
    return 'small';
  });

  // Pour compatibilité avec les composants existants
  const isDesktop = screenSize !== 'small';

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1200) setScreenSize('large');
      else if (width >= 768) setScreenSize('medium');
      else setScreenSize('small');
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Déterminer les widgets à afficher selon le rôle
  const roleConfig = getRoleConfig(role);
  // Combiner avec les permissions pour les widgets congés
  const showLeaveWidgets = roleConfig.showLeaveCounters && hasLeaveAccess;

  return (
    <div style={styles.container}>
      {/* Section métier (spécifique au rôle) */}
      {roleConfig.hasBusinessSection && (
        <div style={styles.businessSection}>
          {role === 'Employés' && (
            <EmployeeSection token={token} userId={userId} onNavigate={smartNavigate} screenSize={screenSize} showLeaveWidgets={showLeaveWidgets} />
          )}

          {role === 'Chef d\'équipe' && (
            <TeamLeaderSection token={token} onNavigate={smartNavigate} screenSize={screenSize} />
          )}

          {role === 'Planificateur' && (
            <PlannerSection token={token} onNavigate={smartNavigate} screenSize={screenSize} />
          )}

          {role === 'RH' && (
            <HRSection token={token} onNavigate={smartNavigate} screenSize={screenSize} />
          )}

          {role === 'Admin' && (
            <AdminSection token={token} onNavigate={smartNavigate} screenSize={screenSize} />
          )}

          {(role === 'Comptabilité' || role === 'Administrative') && (
            <BusinessPlaceholder role={role} onNavigate={smartNavigate} />
          )}
        </div>
      )}

      {/* Section "Mon espace" - Widgets communs */}
      {roleConfig.hasPersonalSection && (
        <div style={styles.personalSection}>
          <h3 style={styles.sectionTitle}>Mon espace</h3>

          {/* Widget Ma semaine - Caché pour Employés (affiché dans EmployeeSection) */}
          {roleConfig.showWeekOverview && role !== 'Employés' && (
            <WeekOverview
              token={token}
              userId={userId}
              onNavigate={smartNavigate}
            />
          )}

          {/* Widgets compteurs CP/RTT - Côte à côte, repliés par défaut */}
          {/* Affiché si permission congés ET config du rôle l'autorise */}
          {showLeaveWidgets && userId && role !== 'Employés' && (
            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '16px',
              flexWrap: screenSize === 'small' ? 'wrap' : 'nowrap'
            }}>
              <div style={{ flex: 1, minWidth: screenSize === 'small' ? '100%' : '280px' }}>
                <LeaveBalanceWidget token={token} />
              </div>
            </div>
          )}

          {/* Widget Calendrier Équipe */}
          {roleConfig.showTeamCalendar && (
            <TeamCalendarWidget
              token={token}
              userRole={role}
            />
          )}
        </div>
      )}

      {/* Calendrier équipe pour les rôles qui n'ont pas de section personnelle */}
      {!roleConfig.hasPersonalSection && roleConfig.showTeamCalendar && (
        <div style={styles.calendarSection}>
          <TeamCalendarWidget
            token={token}
            userRole={role}
          />
        </div>
      )}

      {/* Grille de navigation rapide */}
      {roleConfig.showQuickNav && (
        <QuickNavGrid role={role} onNavigate={smartNavigate} screenSize={screenSize} />
      )}
    </div>
  );
};

/**
 * Section métier Employés - Layout responsive 3/2/1 colonnes
 */
const EmployeeSection = ({ token, userId, onNavigate, screenSize, showLeaveWidgets = true }) => {
  const gridCols = screenSize === 'large' ? '1fr 1fr 1fr' : screenSize === 'medium' ? '1fr 1fr' : '1fr';
  const isSmall = screenSize === 'small';

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: gridCols,
      gap: '20px',
      alignItems: 'start'
    }}>
      {/* Widget 1 : Prochaine prestation */}
      <div>
        <NextPrestation token={token} userId={userId} onNavigate={onNavigate} />
      </div>

      {/* Widget 2 : Ma semaine */}
      <div>
        <WeekOverview token={token} userId={userId} onNavigate={onNavigate} />
      </div>

      {/* Widget 3 : Compteurs CP/RTT (en colonne sur grand écran, à part sur les autres) */}
      {/* Affiché seulement si l'utilisateur a la permission congés */}
      {showLeaveWidgets && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          gridColumn: isSmall ? '1' : undefined
        }}>
          <LeaveBalanceWidget token={token} />
        </div>
      )}
    </div>
  );
};

/**
 * Section métier Chef d'équipe - Layout responsive 3/2/1 colonnes
 */
const TeamLeaderSection = ({ token, onNavigate, screenSize }) => {
  const gridCols = screenSize === 'large' ? '1fr 1fr 1fr' : screenSize === 'medium' ? '1fr 1fr' : '1fr';

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: gridCols,
      gap: '20px',
      alignItems: 'start'
    }}>
      <TodayPrestation token={token} onNavigate={onNavigate} />
      <TeamStatus token={token} onNavigate={onNavigate} />
      <div style={{ gridColumn: screenSize === 'small' ? '1' : screenSize === 'large' ? '3' : '1 / -1' }}>
        <PendingWorksheets token={token} onNavigate={onNavigate} />
      </div>
    </div>
  );
};

/**
 * Section métier Planificateur - Layout responsive 3/2/1 colonnes
 */
const PlannerSection = ({ token, onNavigate, screenSize }) => {
  const gridCols = screenSize === 'large' ? '1fr 1fr 1fr' : screenSize === 'medium' ? '1fr 1fr' : '1fr';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Widget intérimaires à qualifier (affiché uniquement si des intérimaires en attente) */}
      <PendingInterimsWidget token={token} onNavigate={onNavigate} />

      <div style={{
        display: 'grid',
        gridTemplateColumns: gridCols,
        gap: '20px',
        alignItems: 'start'
      }}>
        <AlertsList token={token} onNavigate={onNavigate} />
        <BlockedList token={token} onNavigate={onNavigate} />
        <AvailableUsers token={token} onNavigate={onNavigate} />
        <div style={{ gridColumn: screenSize === 'small' ? '1' : '1 / -1' }}>
          <GroupWorkload token={token} onNavigate={onNavigate} />
        </div>
      </div>
      {/* Widget Rappels J-1 */}
      <div style={{ maxWidth: screenSize === 'large' ? '400px' : '100%' }}>
        <SendRemindersWidget token={token} />
      </div>
    </div>
  );
};

/**
 * Section métier RH - Layout responsive 3/2/1 colonnes
 */
const HRSection = ({ token, onNavigate, screenSize }) => {
  const gridCols = screenSize === 'large' ? '1fr 1fr 1fr' : screenSize === 'medium' ? '1fr 1fr' : '1fr';

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: gridCols,
      gap: '20px',
      alignItems: 'start'
    }}>
      <WorksheetsToValidate token={token} onNavigate={onNavigate} />
      <LeaveRequestsWidget token={token} onNavigate={onNavigate} />
      <AbsentsToday token={token} />
      <div style={{ gridColumn: screenSize === 'small' ? '1' : '1 / -1' }}>
        <LeaveStats token={token} onNavigate={onNavigate} />
      </div>
    </div>
  );
};

/**
 * Section métier Admin - Layout responsive 3/2/1 colonnes
 */
const AdminSection = ({ token, onNavigate, screenSize }) => {
  const gridCols = screenSize === 'large' ? '1fr 1fr 1fr' : screenSize === 'medium' ? '1fr 1fr' : '1fr';
  const isSmall = screenSize === 'small';
  const isLarge = screenSize === 'large';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Widget intérimaires à qualifier (affiché uniquement si des intérimaires en attente) */}
      <PendingInterimsWidget token={token} onNavigate={onNavigate} />

      {/* Ligne 1 : 3 widgets - même hauteur */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: gridCols,
        gap: '20px',
        alignItems: 'stretch'  // Force même hauteur
      }}>
        <ConnectedUsers token={token} />
        <WorkforceStatus token={token} />
        <SystemHealth token={token} onNavigate={onNavigate} />
      </div>
      {/* Ligne 2 : Rappels + Activité - même hauteur */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isLarge ? '1fr 1fr' : '1fr',
        gap: '20px',
        alignItems: 'stretch'
      }}>
        <SendRemindersWidget token={token} />
        <RecentActivity token={token} onNavigate={onNavigate} />
      </div>
      {/* Ligne 3 : Calendrier équipe */}
      <TeamCalendarWidget token={token} userRole="Admin" />
    </div>
  );
};

/**
 * Configuration des widgets par rôle
 */
const getRoleConfig = (role) => {
  const configs = {
    'Admin': {
      hasBusinessSection: true,
      hasPersonalSection: false,
      showWeekOverview: false,
      showLeaveCounters: false,
      showTeamCalendar: false,  // Intégré directement dans AdminSection
      showQuickNav: true
    },
    'Planificateur': {
      hasBusinessSection: true,
      hasPersonalSection: true,
      showWeekOverview: false,
      showLeaveCounters: true,
      showTeamCalendar: true,
      showQuickNav: true
    },
    'RH': {
      hasBusinessSection: true,
      hasPersonalSection: true,
      showWeekOverview: false,
      showLeaveCounters: true,
      showTeamCalendar: true,
      showQuickNav: false
    },
    'Chef d\'équipe': {
      hasBusinessSection: true,
      hasPersonalSection: true,
      showWeekOverview: false,
      showLeaveCounters: true,
      showTeamCalendar: true,
      showQuickNav: false
    },
    'Employés': {
      hasBusinessSection: true,
      hasPersonalSection: true,
      showWeekOverview: true,
      showLeaveCounters: true,
      showTeamCalendar: false,
      showQuickNav: false
    },
    'Comptabilité': {
      hasBusinessSection: true,
      hasPersonalSection: true,
      showWeekOverview: true,
      showLeaveCounters: true,
      showTeamCalendar: false,
      showQuickNav: false
    },
    'Administrative': {
      hasBusinessSection: true,
      hasPersonalSection: true,
      showWeekOverview: true,
      showLeaveCounters: true,
      showTeamCalendar: false,
      showQuickNav: false
    }
  };

  return configs[role] || configs['Employés'];
};

/**
 * Grille de navigation rapide vers les modules - Layout responsive
 */
const QuickNavGrid = ({ role, onNavigate, screenSize }) => {
  const quickLinks = [
    { id: 'planning', name: 'Planning', icon: '📅', color: '#198754' },
    { id: 'groups', name: 'Groupes', icon: '🚛', color: '#6f42c1' },
    { id: 'clients', name: 'Clients', icon: '🏰', color: '#fd7e14' },
    { id: 'locations', name: 'Lieux', icon: '📍', color: '#20c997' }
  ];

  // Ajouter des liens admin si nécessaire
  if (role === 'Admin') {
    quickLinks.push(
      { id: 'users', name: 'Utilisateurs', icon: '👥', color: '#0dcaf0' },
      { id: 'analytics', name: 'Analytics', icon: '📈', color: '#9c27b0' }
    );
  }

  // Grille responsive : 6 cols desktop, 4 cols tablette, 2 cols mobile
  const gridCols = screenSize === 'large' ? 'repeat(6, 1fr)' : screenSize === 'medium' ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)';

  return (
    <div style={styles.quickNavContainer}>
      <h4 style={styles.quickNavTitle}>Accès rapide</h4>
      <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '12px' }}>
        {quickLinks.map(link => (
          <button
            key={link.id}
            style={{
              ...styles.quickNavButton,
              borderColor: link.color
            }}
            onClick={() => onNavigate && onNavigate(link.id)}
          >
            <span style={styles.quickNavIcon}>{link.icon}</span>
            <span style={styles.quickNavLabel}>{link.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const styles = {
  container: {
    width: '95%',           // Utilise 95% de la largeur disponible
    maxWidth: '1800px',     // Limite max pour très grands écrans
    margin: '0 auto',
    padding: '20px'
  },
  businessSection: {
    marginBottom: '24px'
  },
  personalSection: {
    marginBottom: '24px'
  },
  sectionTitle: {
    margin: '0 0 16px 0',
    fontSize: '1.2rem',
    fontWeight: '600',
    color: '#1a1a2e',
    borderBottom: '2px solid #e9ecef',
    paddingBottom: '8px'
  },
  calendarSection: {
    marginBottom: '24px'
  },
  quickNavContainer: {
    marginTop: '24px',
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  quickNavTitle: {
    margin: '0 0 16px 0',
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  quickNavButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '16px',
    backgroundColor: '#f8f9fa',
    border: '2px solid',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '1rem'
  },
  quickNavIcon: {
    fontSize: '1.5rem'
  },
  quickNavLabel: {
    fontSize: '0.85rem',
    fontWeight: '500',
    color: '#1a1a2e'
  }
};

export default DashboardContainer;
